/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
// TODO: Fix the above issues to improve accessibility, then remove the eslint comments.
import { cloneElement, useEffect, useMemo, useRef, useState } from 'react';
import { bool, element, func, number, object, oneOf, string } from 'prop-types';
import clsx from 'clsx';
import { defaults, isEmpty, isFunction } from 'lodash';
import moment from 'moment';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { Close as CloseIcon, Info as InfoIcon } from '@material-ui/icons';
import { ErrorBoundary } from 'react-error-boundary';
import { plural, t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';

import ErrorMessage from '../../error-message/ErrorMessage';
import { filterPropTypes } from './FilterPropTypes';
import FilterRegistry from './FilterRegistry';
import { useFilterControl } from './FilterControl';
import {
    useCurrentFilterStateById,
    useFilterState,
    useSummarizedFilterStateById,
} from '../../../stores/FilterStateStore';
import { usePageState } from '../../../stores/PageStateStore';
import { middleEllipsis } from '../../../utilities/StringUtilities';
import useStyles from '../filter-container/FilterContainerStyles';
import { AutoRefresh as AutoRefreshIcon, Refresh as RefreshIcon } from '../../icon/Icons';
import Link, { buildLinkTooltip, buildLinkUrl } from '../../links/Link';
import { SessionStorageUtilities } from '../../../utilities/StorageUtilities';

export const SUMMARY_LENGTHS = {
    DEFAULT: 'default',
    SHORT: 'short',
};

function getRefreshIntervalText(refreshInterval) {
    const duration = moment.duration(refreshInterval, 'milliseconds');
    const seconds = duration.seconds();
    const minutes = duration.minutes();
    const hours = duration.hours();

    if (seconds > 0) {
        return i18n._(
            plural(seconds, {
                one: `(auto refresh interval: # second)`,
                other: `(auto refresh interval: # seconds)`,
            })
        );
    }

    if (minutes > 0) {
        return i18n._(
            plural(minutes, {
                one: `(auto refresh interval: # minute)`,
                other: `(auto refresh interval: # minutes)`,
            })
        );
    }

    return i18n._(
        plural(hours, {
            one: `(auto refresh interval: # hour)`,
            other: `(auto refresh interval: # hours)`,
        })
    );
}

function Summary({
    classes,
    onRemove,
    onEdit,
    onRefresh,
    filterId,
    filterOptions,
    required,
    enabled,
    refreshes,
    refreshInterval,
    title,
    summaryLength,
    maxWidth = 65,
}) {
    const [summarizedState] = useSummarizedFilterStateById(filterId);
    const infoMessage = useMemo(() => {
        return summarizedState
            ? summarizedState.infoMessage
            : i18n._(t`This filter could not be initialized with a value`);
    }, [summarizedState]);
    const summary = useMemo(() => {
        return summaryLength === SUMMARY_LENGTHS.DEFAULT ? summarizedState?.value : summarizedState?.shortValue;
    }, [summaryLength, summarizedState]);
    const summaryValue = useMemo(() => (summary ? middleEllipsis(summary, maxWidth) : '--'), [summary, maxWidth]);
    const [isAuto, autoIntervalText] = useMemo(() => {
        const auto = refreshInterval >= 0;
        return [auto, auto ? ` ${getRefreshIntervalText(refreshInterval)}` : ''];
    }, [refreshInterval]);
    const [currentState] = useCurrentFilterStateById(filterId);
    const [filterState] = useFilterState();
    const [pageState] = usePageState();
    const { link } = filterOptions || {};
    const linkUrl = useMemo(() => {
        if (!link) return null;
        return buildLinkUrl(link, currentState, filterState, pageState);
    }, [link, currentState, filterState, pageState]);
    const linkTooltip = useMemo(() => {
        if (!link) return null;
        return buildLinkTooltip(link, currentState);
    }, [link, currentState]);

    function handleClick() {
        if (enabled) onEdit();
    }

    return summarizedState || required ? (
        <div id={`filter-${filterId}`} className={classes.filter}>
            <div className={clsx(classes.filterSummary, { [classes.disabledFilterSummary]: !enabled })}>
                <label className={classes.filterLabel}>{i18n._(title)}</label>
                <div id={`filter-action-edit-${filterId}`} className={classes.filterValue} onClick={handleClick}>
                    {!enabled && linkUrl ? (
                        <Link to={linkUrl} title={linkTooltip}>
                            {summaryValue}
                        </Link>
                    ) : (
                        <span>{summaryValue}</span>
                    )}
                    {infoMessage && (
                        <div className={classes.summaryInfo} title={infoMessage}>
                            <InfoIcon fontSize="medium" />
                        </div>
                    )}
                </div>
            </div>
            {refreshes && (
                <Tooltip title={`${t`Refresh the page data`}${autoIntervalText}`} placement="bottom">
                    <IconButton
                        id={`filter-action-refresh-${filterId}`}
                        className={classes.filterButton}
                        onClick={onRefresh}
                        disableRipple
                        disableFocusRipple
                    >
                        {isAuto ? <AutoRefreshIcon /> : <RefreshIcon />}
                    </IconButton>
                </Tooltip>
            )}
            {!required && (
                <Tooltip title={t`Remove this filter`} placement="bottom">
                    <IconButton
                        id={`filter-action-remove-${filterId}`}
                        className={classes.filterButton}
                        onClick={onRemove}
                        disableRipple
                        disableFocusRipple
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </div>
    ) : null;
}

Summary.propTypes = {
    onRemove: func.isRequired,
    onEdit: func.isRequired,
    filterId: string.isRequired,
    filterOptions: object,
    required: bool.isRequired,
    refreshes: bool.isRequired,
    refreshInterval: number.isRequired,
    onRefresh: func.isRequired,
    title: filterPropTypes.title.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    classes: object.isRequired,
    summaryLength: oneOf(Object.values(SUMMARY_LENGTHS)).isRequired,
};

Summary.defaultProps = {
    filterOptions: {},
};

const SHOW_ALL_NONE_BUTTON_MIN_OPTION_LENGTH = 5;
export const isShowAllNoneButton = (options) =>
    !isEmpty(options) && Array.isArray(options) && options.length > SHOW_ALL_NONE_BUTTON_MIN_OPTION_LENGTH;
export default function Filter({
    children: child,
    onChange,
    onRemove,
    classes,
    summaryLength = SUMMARY_LENGTHS.DEFAULT,
    required: requiredOverride = false,
    autoRefresh: autoRefreshOverride = false,
    enabled = true,
}) {
    const defaultClasses = useStyles();
    const resolvedClasses = useMemo(() => defaults({}, classes, defaultClasses), [classes, defaultClasses]);
    const {
        id: filterId,
        required: childRequired = false,
        title,
        state,
        autoApply = false,
        autoRefresh: childAutoRefresh = false,
        options: filterOptions,
        maxWidth = 'sm',
        fullWidth = false,
        summaryMaxWidth = 65,
    } = child.props;
    const showAllNoneButton = isShowAllNoneButton(state);
    const required = childRequired || requiredOverride;
    const autoRefresh = childAutoRefresh || autoRefreshOverride;
    const [currentState, filterStateActions] = useCurrentFilterStateById(filterId);
    const [filterStateService] = useState(FilterRegistry.getFilterStateService(filterId, filterOptions));
    const [editing, setEditing] = useState(false);
    const [editedState, setEditedState] = useState(undefined);
    const [valid, setValid] = useState(true);
    const autoRefreshTimeout = useRef();
    const childRef = useRef();
    const { autoRefreshPaused, pauseAutoRefresh, resumeAutoRefresh } = useFilterControl();
    const [canRefresh, shouldRefresh, refreshInterval] = useMemo(() => {
        const can = autoRefresh && !!filterStateService.shouldRefreshState;
        const should =
            can && !autoRefreshPaused && currentState ? filterStateService.shouldRefreshState(currentState) : false;
        const interval = should ? filterStateService.getRefreshInterval(currentState) : -1;
        return [can, should, interval];
    }, [currentState, filterStateService, autoRefresh, autoRefreshPaused]);
    const handleRefresh = useRef();

    handleRefresh.current = () => {
        const nextState = filterStateService.refreshState(currentState);

        if (isFunction(onChange)) {
            onChange(nextState);
        } else {
            filterStateActions.setFilterState(filterId, nextState);
        }
    };

    useEffect(() => {
        if (currentState !== undefined) {
            setEditedState(currentState);
        } else if (!required) {
            pauseAutoRefresh();
            setEditing(true);
        }

        // Stop auto-update when the filter is unmounted.
        return () => {
            clearTimeout(autoRefreshTimeout.current);
        };
        // This intentionally disables the exhaustive dependency check because this is initialization/cleanup meant
        // to run ONLY when the filter is mounted/unmounted, so we don't want it to run when its dependencies change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Start auto-update if the filter supports it.  Currently, only the time range filter does.
    useEffect(() => {
        clearTimeout(autoRefreshTimeout.current);

        if (shouldRefresh) {
            autoRefreshTimeout.current = setTimeout(handleRefresh.current, refreshInterval);
        }

        setEditedState(currentState);
    }, [filterId, currentState, filterStateActions, filterStateService, shouldRefresh, refreshInterval]);

    function applyState(newState) {
        if (!newState) return;

        resumeAutoRefresh();
        clearTimeout(autoRefreshTimeout.current);

        setEditing(false);

        if (isFunction(onChange)) {
            onChange(newState);
        } else {
            filterStateActions.setFilterState(filterId, newState);
        }
    }

    function handleRemove() {
        if (isFunction(onRemove)) {
            onRemove();
        } else {
            filterStateActions.clearFilterState(filterId);
        }
    }

    function handleBeginEdit() {
        pauseAutoRefresh();
        setEditing(true);
    }

    function handleApplyEdit() {
        const finalState = childRef.current?.getCurrentState?.() ?? editedState;
        SessionStorageUtilities.setJSONValue(filterId, finalState);
        applyState(finalState);
    }

    function handleCancelEdit() {
        setEditing(false);
        resumeAutoRefresh();
        if (currentState === undefined && !required) {
            if (isFunction(onRemove)) {
                onRemove();
            } else {
                filterStateActions.clearFilterState(filterId);
            }
        } else {
            setEditedState(currentState);
        }
    }

    function handleSelectAllEdit() {
        let allState = [];
        if (isFunction(childRef.current?.getAllState)) {
            allState = childRef.current.getAllState();
        } else if (Array.isArray(child.props.options?.[filterId])) {
            allState = child.props.options[filterId].map((opt) => opt.value);
        } else if (Array.isArray(child.type?.defaultProps?.state)) {
            allState = child.type.defaultProps.state;
        }

        setEditedState(allState);

        if (autoApply) {
            applyState(allState);
        }
    }

    function handleSelectNoneEdit() {
        const noneState = [];
        setEditedState(noneState);
        if (autoApply) {
            applyState(noneState);
        }
    }

    function renderFilterComponent() {
        return cloneElement(child, {
            innerRef: childRef,
            state: editedState,
            filterActions: {
                setState: (newState) => (autoApply ? applyState(newState) : setEditedState(newState)),
                setValid,
            },
        });
    }

    return (
        <>
            <Summary
                classes={resolvedClasses}
                filterId={filterId}
                filterOptions={filterOptions}
                title={title}
                summaryLength={summaryLength}
                required={required}
                enabled={enabled}
                refreshes={canRefresh}
                refreshInterval={refreshInterval}
                onRefresh={handleRefresh.current}
                onEdit={handleBeginEdit}
                onRemove={handleRemove}
                maxWidth={summaryMaxWidth}
            />
            <Dialog open={editing} onClose={handleCancelEdit} maxWidth={maxWidth} fullWidth={fullWidth}>
                <DialogTitle className={resolvedClasses.dialogTitle} disableTypography>
                    <Typography className={resolvedClasses.dialogTitleText} variant="h6">
                        {i18n._(title)}
                    </Typography>
                    <IconButton
                        aria-label="cancel"
                        className={resolvedClasses.dialogCloseButton}
                        onClick={handleCancelEdit}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className="flex-container" id={`filter-edit-content-${filterId}`}>
                    <ErrorBoundary FallbackComponent={ErrorMessage}>{renderFilterComponent()}</ErrorBoundary>
                </DialogContent>
                <DialogActions id={`filter-edit-actions-${filterId}`}>
                    {showAllNoneButton && (
                        <Button id={`filter-edit-action-select-all-${filterId}`} onClick={handleSelectAllEdit}>
                            <Trans>All</Trans>
                        </Button>
                    )}
                    {showAllNoneButton && (
                        <Button id={`filter-edit-action--select-none-${filterId}`} onClick={handleSelectNoneEdit}>
                            <Trans>None</Trans>
                        </Button>
                    )}
                    <Button id={`filter-edit-action-cancel-${filterId}`} onClick={handleCancelEdit}>
                        <Trans>Cancel</Trans>
                    </Button>
                    {autoApply ? null : (
                        <Button
                            id={`filter-edit-action-apply-${filterId}`}
                            onClick={handleApplyEdit}
                            color="primary"
                            disabled={!valid}
                        >
                            <Trans>Apply</Trans>
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

Filter.propTypes = {
    children: element.isRequired,
    onChange: func,
    onRemove: func,
    classes: object,
};

Filter.defaultProps = {
    onChange: undefined,
    onRemove: undefined,
    classes: undefined,
};
